import { inject } from '@angular/core';
import {
  TranslateLoader,
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateModuleConfig,
} from '@ngx-translate/core';
import { isMatchWith } from 'lodash-es';
import { forkJoin, from, catchError, of, map, switchMap } from 'rxjs';

import { Override } from '@plugins/shared/types/auth';
import { SDK, STORE } from '@plugins/middleware-sdk';

import { TranslationsImport } from './lib-translation.module';

export interface TranslationModuleConfig
  extends Omit<TranslateModuleConfig, 'loader' | 'missingTranslationHandler'> {
  imports?: (lang: string) => Promise<TranslationsImport>[];
}

export function translationModuleConfig({
  imports,
  ...rest
}: TranslationModuleConfig): TranslateModuleConfig {
  const config: TranslateModuleConfig = { ...rest };
  if (imports) {
    config.loader = {
      provide: TranslateLoader,
      useFactory: () => new LazyTranslationLoader(imports),
    };
    config.missingTranslationHandler = commonMissingTranslationHandler;
  }
  return config;
}

export class LazyTranslationLoader implements TranslateLoader {
  constructor(
    private imports: (lang: string) => Promise<TranslationsImport>[],
  ) {}

  private sdk = inject(SDK);
  private store = inject(STORE);

  getTranslation(lang: string) {
    const defaultTranslations = forkJoin(
      this.imports(lang).map((i) =>
        from(i).pipe(catchError(() => of({ default: {} }))),
      ),
    ).pipe(
      map((translations) =>
        translations.reduce((acc, curr) => {
          acc = { ...acc, ...curr.default };
          return acc;
        }, {}),
      ),
    );

    const storeTranslations = from(
      this.sdk.isOverridePresent(Override.i18n),
    ).pipe(
      switchMap((isOverridePresent) =>
        isOverridePresent
          ? this.store.get('tenant', `i18n:${lang}`).catch(() => of({}))
          : of({}),
      ),
    );

    return forkJoin([defaultTranslations, storeTranslations]).pipe(
      map(([defaultTranslations, storeTranslations]) => {
        if (
          isTranslation(storeTranslations) &&
          isPartialOf(defaultTranslations, storeTranslations)
        ) {
          return mergeTranslations(defaultTranslations, storeTranslations);
        }
        return defaultTranslations;
      }),
    );
  }
}

const commonMissingTranslationHandler = {
  provide: MissingTranslationHandler,
  useValue: {
    handle: (params: MissingTranslationHandlerParams) =>
      missingTranslationString(params.key),
  },
};

export function missingTranslationString(key: string) {
  return `<i18n:${key}>`;
}

function isTranslation(obj: unknown): obj is Record<string, object | string> {
  if (typeof obj !== 'object' || obj === null) return false;

  for (const key in obj)
    if (typeof key !== 'string' && typeof obj[key] !== 'object') return false;

  return true;
}

export const isPartialOf = (
  target: object | string,
  source: object | string,
) => {
  if (typeof target === 'string' && typeof source === 'string') return true;
  if (typeof target !== 'object' || typeof source !== 'object') return false;
  return isMatchWith(target, source, isPartialOf);
};

export function mergeTranslations(
  target: Record<string, object | string>,
  source: Record<string, object | string>,
): Record<string, object | string> {
  for (const key in source) {
    const targetValue = target[key];
    const sourceValue = source[key];
    if (typeof sourceValue === 'string') target[key] = sourceValue;
    else if (isTranslation(targetValue) && isTranslation(sourceValue))
      target[key] = mergeTranslations(targetValue, sourceValue);
  }
  return target;
}
